// src/components/ImageModal.tsx
import React, { useEffect } from 'react';
import { X } from 'lucide-react';

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  imageAlt: string;
  title?: string;
  description?: string;
}

const ImageModal: React.FC<ImageModalProps> = ({
  isOpen,
  onClose,
  imageSrc,
  imageAlt}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full h-full max-w-7xl max-h-[95vh] flex items-center justify-center">
        
        {/* Container for image and close button */}
        <div className="relative">
          {/* Close button - positioned completely outside the frame */}
          <button
            onClick={onClose}
            className="absolute -top-12 -right-12 z-20 glass-card p-3 text-white hover:bg-white/20 transition-all duration-300 hover:scale-110 focus:outline-hidden focus:ring-2 focus:ring-purple-400 group rounded-full bg-black/70 backdrop-blur-sm border border-white/30"
            aria-label="Zapri sliko"
          >
            <X className="w-6 h-6 group-hover:text-purple-300 transition-colors" />
          </button>

          {/* Image container with rounded frame */}
          <div className="glass-card p-6 rounded-3xl artistic-border max-w-[90vw] max-h-[85vh] flex items-center justify-center">
            <img
              src={imageSrc}
              alt={imageAlt}
              className="max-w-full max-h-[75vh] w-auto h-auto object-contain rounded-2xl"
              style={{ minWidth: '600px', minHeight: '400px' }}
            />
          </div>
        </div>

        {/* Click outside to close */}
        <div 
          className="absolute inset-0 -z-10"
          onClick={onClose}
          aria-label="Zapri sliko"
        />
      </div>
    </div>
  );
};

export default ImageModal;